const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function getToken() {
  return localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
}

function authHeaders({ json = false } = {}) {
  const token = getToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

export function parseApiError(payload, fallback) {
  if (!payload || typeof payload !== 'object') return fallback;
  return (
    payload.message ||
    payload.error?.message ||
    (Array.isArray(payload.errors) && payload.errors[0]?.message) ||
    fallback
  );
}

/**
 * Normalizes one category record from various possible API shapes.
 */
export function normalizeCategory(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw.id;
  const name = raw.name ?? '';
  const slug = raw.slug ?? '';
  const description = raw.description ?? '';
  const displayOrder = Number(raw.display_order ?? raw.displayOrder ?? 0);
  const isActive =
    raw.is_active !== undefined
      ? Boolean(raw.is_active)
      : raw.active !== undefined
        ? Boolean(raw.active)
        : raw.status === 'active' || raw.status === undefined;
  const deletedAt = raw.deleted_at ?? raw.deletedAt ?? null;
  const createdAt = raw.created_at ?? raw.createdAt ?? null;
  const updatedAt = raw.updated_at ?? raw.updatedAt ?? null;

  return {
    id,
    name: String(name),
    slug: String(slug),
    description: String(description),
    display_order: Number.isFinite(displayOrder) ? displayOrder : 0,
    is_active: isActive,
    deleted_at: deletedAt,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

export function extractCategoryList(json) {
  const d = json?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.categories)) return d.categories;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.records)) return d.records;
  if (Array.isArray(json?.categories)) return json.categories;
  return [];
}

export async function listCategories() {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/advisory-categories`, {
    method: 'GET',
    headers: authHeaders({ json: false }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(parseApiError(json, 'Failed to load categories'));
    err.status = res.status;
    throw err;
  }
  const data = json?.success ? json.data : json;
  return extractCategoryList({ data }).map(normalizeCategory).filter(Boolean);
}

export async function createCategory(body) {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/advisory-categories`, {
    method: 'POST',
    headers: authHeaders({ json: true }),
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(parseApiError(json, 'Failed to create category'));
    err.status = res.status;
    throw err;
  }
  return json?.success ? json.data : json;
}

export async function updateCategory(id, body) {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/advisory-categories/${id}`, {
    method: 'PATCH',
    headers: authHeaders({ json: true }),
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(parseApiError(json, 'Failed to update category'));
    err.status = res.status;
    throw err;
  }
  return json?.success ? json.data : json;
}

export async function toggleCategory(id, is_active) {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/advisory-categories/${id}/toggle`, {
    method: 'PATCH',
    headers: authHeaders({ json: true }),
    body: JSON.stringify({ is_active }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(parseApiError(json, 'Failed to update status'));
    err.status = res.status;
    throw err;
  }
  return json?.success ? json.data : json;
}

export async function deleteCategory(id) {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/advisory-categories/${id}`, {
    method: 'DELETE',
    headers: authHeaders({ json: false }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(parseApiError(json, 'Failed to delete category'));
    err.status = res.status;
    throw err;
  }
  return json?.success ? json.data : json;
}
